import { FormEvent } from 'react';
import type { CatalogItem } from '../../types/catalog';
import { Form, FormField, FormLabel, FormInput, FormSelect, FormTextarea, Button } from '../ui';
import FileUploader from './FileUploader';

interface CatalogFormProps {
  item: CatalogItem | null;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  uploadError?: string;
}

function CatalogForm({
  item,
  onSubmit,
  isLoading,
  onClose,
  onFileSelect,
  selectedFile,
  uploadError,
}: CatalogFormProps) {
  // Function to truncate file name
  const truncateFileName = (fileName: string, maxLength: number = 30) => {
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.split('.').pop() || '';
    const nameWithoutExt = fileName.slice(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3);

    return `${truncatedName}...${extension}`;
  };

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="type" required>
          Tipo
        </FormLabel>
        <FormSelect id="type" name="type" defaultValue={item?.type} required>
          <option value="">Selecione um tipo</option>
          <option value="manual">Manual</option>
          <option value="documento">Documento</option>
          <option value="imagem">Imagem</option>
        </FormSelect>
      </FormField>

      <FormField>
        <FormLabel htmlFor="title" required>
          Título
        </FormLabel>
        <FormInput id="title" name="title" defaultValue={item?.title} required />
      </FormField>

      <FormField>
        <FormLabel htmlFor="summary">Resumo</FormLabel>
        <FormTextarea id="summary" name="summary" defaultValue={item?.summary} rows={3} />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="version">Versão</FormLabel>
          <FormInput id="version" name="version" defaultValue={item?.specifications?.version} />
        </FormField>

        <FormField>
          <FormLabel htmlFor="language">Idioma</FormLabel>
          <FormSelect id="language" name="language" defaultValue={item?.specifications?.language}>
            <option value="">Selecione um idioma</option>
            <option value="pt-BR">Português (Brasil)</option>
            <option value="en">Inglês</option>
            <option value="es">Espanhol</option>
          </FormSelect>
        </FormField>
      </div>

      {!item && (
        <FormField>
          <FormLabel htmlFor="file" required>
            Arquivo
          </FormLabel>
          <FileUploader
            onDrop={files => {
              if (files.length > 0) {
                onFileSelect(files[0]);
              }
            }}
          />
          {selectedFile && (
            <p className="mt-2 text-sm text-gray-600">
              Arquivo selecionado: {truncateFileName(selectedFile.name)}
            </p>
          )}
          {uploadError && <p className="mt-1 text-sm text-red-600">{uploadError}</p>}
        </FormField>
      )}

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button
          type="submit"
          isLoading={isLoading}
          className="sm:col-start-2"
          disabled={!item && !selectedFile}
        >
          {item ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default CatalogForm;
